import { useTheme } from '@mui/material/styles';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types';
import {
    ResultsTableCell, ResultsTableRow, ResultsFooter,
    ResultsTableTitleTypography,
    CustomWidthTooltip
} from '../CustomStyles/StyledComponents'
import { addCommas } from '../../helpers/stringFormatHelper';
import { resultsDataMap } from '../../helpers/reportResultsHelper';
import { isNullish  } from '../../helpers/genericHelper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Cyrb53 } from "../../helpers/hashHelper";

const ExtendedResultsCell = ({ cellData, sx = {}, isOL = false, displayZero = false, ...props }) => {
    const theme = useTheme();
    const DASH = '--';
    let data;
    if(Number.isFinite(cellData)){
        data = isNullish(cellData)?DASH:cellData;
        if(!displayZero){
            data=DASH
        }

    }else if(isOL && !displayZero){
        data=DASH
    }else{
        data = cellData || DASH
    }

    if (isOL) {
        const lvlMap = new Map([['Low', theme.agc.text.benchmarks.low],
        ['Medium', theme.agc.text.benchmarks.medium],
        ['High', theme.agc.text.benchmarks.high],
        ['--', theme.palette.text.primary]]);

        let txtColor = lvlMap.get(data) || theme.palette.text.primary
        // We override the text color based on the Opportunity level
        sx.color = txtColor

    }
    return <ResultsTableCell {...props} sx={sx}>{data} </ResultsTableCell>
}

ExtendedResultsCell.propTypes = {
    cellData: PropTypes.any,
    sx: PropTypes.object,
    isOL: PropTypes.bool,
    displayZero: PropTypes.bool,
};


const DataTable = ({ tableId, tableData, resultsData, emissionUnit = '', media, isEnterpriseTbl = false }) => {

    const getTableRow = (row, tableData) => {
        let headerColumns = tableData.columns
        if (tableData.columnFilter) {
            headerColumns = tableData.columnFilter(tableData.columns, resultsData)
        }
        let displayZero = tableData.olDisplayZero?tableData.olDisplayZero(row):false
        return headerColumns.map((column, idx) => {
            let cellId = column.key
            let cell = row[cellId]
            let eStyle = tableData?.bStyles?.[cellId]
            return (
                <ExtendedResultsCell media={media}
                    key={Cyrb53(`${tableData?.tableTitle}${cellId}${cell}`, idx)}
                    sx={eStyle}
                    isOL={column.isOL}
                    displayZero={displayZero}
                    cellData={cell ? addCommas(cell) : cell} />)
        })
    }
    const getTableBody = (resultsData, tableData, hasFooter) => {
        let bodyData = resultsData
        let footerData = []
        if (hasFooter) {
            bodyData = resultsData.slice(0, resultsData.length - 1)
            footerData = resultsData.slice(resultsData.length - 1)
        }
        return (
            <>
                <TableBody sx={!hasFooter && {borderBottom: '1px solid',}}>
                    {resultsDataMap(bodyData, ([rowId, row]) => {
                        return <ResultsTableRow> {getTableRow(row, tableData)} </ResultsTableRow>
                    })}
                </TableBody>
                {
                    hasFooter && <ResultsFooter sx={media && { mb: 1 }}><ResultsTableRow>{getTableRow(footerData[0], tableData)}</ResultsTableRow></ResultsFooter>
                }
            </>
        )
    }
    const getDivDangerousInnerHTML = (text) => {
        return <div dangerouslySetInnerHTML={{ __html: text }}  />
    }

    const renderTableTitleTooltip = (text) => {
        return (
            <CustomWidthTooltip title={getDivDangerousInnerHTML(text)} placement="bottom-start">
                <InfoOutlinedIcon sx={{ marginLeft: '2px', verticalAlign: 'top', cursor: 'pointer' }} fontSize='small' />
            </CustomWidthTooltip>)
    }

    const cWidth = (idx,len)=>{
        // headering column is 20% of the width, the rest is spread evenly
        return idx===0?20:(80/len);
    }
    const getTableHeader = (tableData, resultsData, media, emissionUnit) => {
        let headerColumns=tableData.columns
        if(tableData.columnFilter){
            headerColumns=tableData.columnFilter(tableData.columns,resultsData)
        }
        let l = headerColumns.length
        return (
            headerColumns.map((column, idx) => {
                let sxData= tableData?.hStyles?.[column.key] 
                            || { width: `${cWidth(idx, l)}%`,
                                 fontStyle: 'none' }
                if(idx === 0){
                    sxData['textAlign']='left'
                }
                let keydata=Cyrb53(`${tableData?.tableTitle}${column.key}`, idx)
                let headerTooltip=tableData?.hTooltips?.[column.key]
                let marker = headerTooltip?.marker?`<sup>${headerTooltip?.marker}</sup>`:''
                let customTooltip = headerTooltip?.responsive?getDivDangerousInnerHTML(headerTooltip.text):''
                return (
                    <ResultsTableCell media={media} key={keydata} sx={sxData}>
                        <CustomWidthTooltip title={customTooltip}>
                            {getDivDangerousInnerHTML(
                                `${column?.isDynamic ? emissionUnit : column.title} ${marker}` )}
                        </CustomWidthTooltip>
                    </ResultsTableCell>
                )
            })
        )
    }
    if (!tableData || !resultsData) {
        return <></>
    }
    
    let isCarbonStockChanges = (tableData?.tableTitle === 'Carbon Stock Changes')
    let tContainerSx = { mb: 3 } // defaults to screen
    if (media === "print") {
        tContainerSx = { marginTop: '4mm' }
    }
    let resultsValues = isCarbonStockChanges?resultsData?.results:resultsData
    
    return <>
        <TableContainer media={media} component={(media === "print") && 'Paper'} sx={tContainerSx}>
            <ResultsTableTitleTypography sx={{ mb: 2 }}>{tableData.tableTitle}
                {tableData?.tableTitleTooltip &&
                    ((media === "screen" && renderTableTitleTooltip(tableData.tableTitleTooltip))
                        || (media === "print" && <sup>{tableData?.tableTitleTTIcon}</sup>))}
            </ResultsTableTitleTypography>
            <Table>
                <TableHead>
                    <TableRow>
                        {getTableHeader(tableData, resultsData, media, emissionUnit)}
                    </TableRow>
                </TableHead>
                {getTableBody(resultsValues, tableData, !tableData.noFooter)}
            </Table>
        </TableContainer>
        {tableData?.hTooltips &&
            Object.entries(tableData.hTooltips).map(([heading, hTooltip]) => {
                if ((!hTooltip.responsive || media === "print") && hTooltip.displayCondition(resultsData)) {
                    let sxData=  media === "print"? { marginTop: '20px' }:null
                    return (
                        <Grid key={heading} item xs={12} sx={sxData}>
                            <Typography variant={media === "print" ? "tablecaption" : "body2"} paragraph>
                                {getDivDangerousInnerHTML(`${hTooltip.marker}${hTooltip.text}`)}
                            </Typography>
                        </Grid>)
                }
                return <></>
            })
        }
        {/* TODO  UI Later as this changes the look and feel */
          tableData?.tableTooltips && media === "print" &&
            tableData?.tableTooltips.map((tt,idx)=>{
                return (
                     <Grid key={idx} item xs={12} mt={media === "print"?2:null}>
                            <Typography variant={media === "print" ? "tablecaption" : "body2"} paragraph>
                                {`${tt}`}
                            </Typography>
                        </Grid>
                )
            })
            }
        {tableData?.tableTitleTooltip && media === "print" &&
            <Grid item xs={12} style={{ marginTop: '20px' }}>
                <Typography variant="tablecaption" paragraph>
                    {getDivDangerousInnerHTML(`<sup>${tableData?.tableTitleTTIcon}</sup> ${tableData?.tableTitleTooltip}`)}
                </Typography>
            </Grid>
        }
    </>
}

DataTable.propTypes = {
    tableId: PropTypes.string,
    tableData: PropTypes.object,
    resultsData: PropTypes.any,
    emissionUnit: PropTypes.string,
    isEnterpriseTbl: PropTypes.bool,
    media: PropTypes.string,
};


export default DataTable;